import { useQuery } from '@tanstack/react-query';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Admin (HRM) businesses API — cross-employee view backed by:
 *   GET /businesses/businesses_all  (scopes.Index + DataAccessControl)
 *   GET /businesses/stats            (scopes.Index + DataAccessControl)
 *   GET /businesses/:id              (scopes.Show  + DataAccessControl)
 *   GET /businesses/:id/related      (scopes.Show)
 *   PUT /businesses/:id/lead/approve (scopes.Approved)
 *   PUT /businesses/:id/bod/approve  (scopes.Admin)
 *
 * Field names confirmed via live probe 2026-08-27.
 */

// ── Types ────────────────────────────────────────────────────────────────────

export interface AdminBusiness {
  business_id: number;
  business_type: string;
  business_place: string;
  business_startdate: string;
  business_enddate: string;
  business_content: string;
  business_reason_cancelled: string | null;
  business_leader_confirm: boolean;
  business_bod_confirm: boolean;
  employee_id: number;
  position_id: number;
  metadata: {
    days: BusinessDay[];
    customer: BusinessCustomer | null;
  } | null;
  business_status: string;
  business_leader_feedback: string | null;
  business_bod_feedback: string | null;
  business_leader_status: string;
  business_bod_status: string;
  business_created_at: string;
  business_updated_at: string;
  employee_name: string;
  department_name: string;
  position_name: string;
}

export interface BusinessDay {
  business_option: string;
  business_startdate: string;
  business_enddate: string;
}

export interface BusinessCustomer {
  customer_id: number;
  customer_name: string;
  customer_phone?: string;
  customer_email?: string;
  company_name?: string;
  customer_journey_id?: number;
}

export interface BusinessStats {
  total_requests: number;
  total_days: number;
  pending_count: number;
  leader_approved_count: number;
  approved_count: number;
  rejected_count: number;
  cancelled_count: number;
  pending_days: number;
  approved_days: number;
  rejected_days: number;
}

export interface BusinessDetail {
  business: AdminBusiness;
  explaination: BusinessExplaination | null;
  parent_business: BusinessParent | null;
}

export interface BusinessExplaination {
  explaination_id: number;
  explaination_result: string | null;
  budget_explaination: number | null;
  expected_date: string | null;
  explaination_created_at: string | null;
  explaination_updated_at: string | null;
  customer_id: number;
  customer_journey_id: number;
}

export interface BusinessParent {
  business_id: number;
  business_type: string;
  business_place: string;
  business_startdate: string;
  business_enddate: string;
  business_content: string;
  business_status: string;
  business_meeting_purpose: string | null;
  employee_id: number;
  employee_name: string;
  employee_code: string;
  department_name: string;
  position_name: string;
}

export interface RelatedBusiness {
  business_id: number;
  business_type: string;
  business_place: string;
  business_startdate: string;
  business_enddate: string;
  business_content: string;
  business_status: string;
  business_meeting_purpose: string | null;
  business_parent_id: number | null;
  employee_id: number;
  employee_name: string;
  employee_code: string;
  department_name: string;
  position_name: string;
  business_role: 'parent' | 'companion';
}

export interface AdminBusinessListParams {
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  q?: string;
  filters?: Record<string, string[]>;
}

// ── Query keys ───────────────────────────────────────────────────────────────

export const BUSINESS_ADMIN_KEYS = {
  list: (params: AdminBusinessListParams) => ['businesses-admin', params] as const,
  stats: () => ['businesses-admin-stats'] as const,
  detail: (id: number | string) => ['businesses-admin', id] as const,
  related: (id: number | string) => ['businesses-admin', id, 'related'] as const,
};

// ── Fetchers ─────────────────────────────────────────────────────────────────

function buildListQuery(params: AdminBusinessListParams): URLSearchParams {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.page_size) qs.set('page_size', String(params.page_size));
  if (params.sort_by) qs.set('sort_by', params.sort_by);
  if (params.sort_order) qs.set('sort_order', params.sort_order);
  if (params.q) qs.set('search', params.q);
  if (params.filters) {
    for (const [field, values] of Object.entries(params.filters)) {
      for (const v of values) qs.append(`filter[${field}]`, v);
    }
  }
  return qs;
}

async function fetchAdminBusinesses(
  params: AdminBusinessListParams,
): Promise<ResponseArray<AdminBusiness>> {
  const qs = buildListQuery(params);
  const res = await dataCentralApi.get<ResponseArray<AdminBusiness>>(
    `/businesses/businesses_all?${qs}`,
  );
  return res.data;
}

async function fetchBusinessStats(): Promise<BusinessStats> {
  const res = await dataCentralApi.get<ResponseObject<BusinessStats>>('/businesses/stats');
  return res.data.result;
}

async function fetchBusinessDetail(id: number | string): Promise<BusinessDetail> {
  const res = await dataCentralApi.get<ResponseObject<BusinessDetail>>(
    `/businesses/${id}`,
  );
  return res.data.result;
}

async function fetchRelatedBusinesses(id: number | string): Promise<RelatedBusiness[]> {
  const res = await dataCentralApi.get<ResponseObject<RelatedBusiness[]>>(
    `/businesses/${id}/related`,
  );
  // API returns result as array directly
  const raw = res.data.result;
  return Array.isArray(raw) ? raw : [];
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

export function useAdminBusinesses(params: AdminBusinessListParams) {
  return useQuery({
    queryKey: BUSINESS_ADMIN_KEYS.list(params),
    queryFn: () => fetchAdminBusinesses(params),
    placeholderData: (prev) => prev,
  });
}

export function useBusinessStats() {
  return useQuery({
    queryKey: BUSINESS_ADMIN_KEYS.stats(),
    queryFn: fetchBusinessStats,
  });
}

export function useBusinessDetail(id: number | string | undefined) {
  return useQuery({
    queryKey: BUSINESS_ADMIN_KEYS.detail(id ?? 0),
    queryFn: () => fetchBusinessDetail(id!),
    enabled: Boolean(id),
  });
}

export function useRelatedBusinesses(id: number | string | undefined) {
  return useQuery({
    queryKey: BUSINESS_ADMIN_KEYS.related(id ?? 0),
    queryFn: () => fetchRelatedBusinesses(id!),
    enabled: Boolean(id),
  });
}
